import React, { MouseEvent } from "react";

import {
  faChevronLeft,
  faChevronRight,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { ErrorBlobDisplay } from "src/base-components/ErrorBlobDisplay";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export type ErrorSection = {
  title: string;
  errorBlob: string;
  isCodeBlock: boolean;
};

type ErrorSelectorProps = {
  setPreviousError: (e: MouseEvent) => void;
  setNextError: (e: MouseEvent) => void;
  displayedFailureArrayIndex: number;
  errors_count: number;
  errorSections: ErrorSection[];
  failureCount: number | undefined;
  resultsOutdated?: boolean;
  outdatedTooltipTitle?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
};

export const ErrorSelector: React.FC<ErrorSelectorProps> = ({
  setPreviousError,
  setNextError,
  displayedFailureArrayIndex,
  errors_count,
  errorSections,
  failureCount,
  action,
  resultsOutdated = false,
  outdatedTooltipTitle = "Test run again to update data",
}) => {
  const iconVariant = resultsOutdated ? "warning" : "error";
  return (
    <div
      className={twJoin(
        "flex max-h-56 w-full rounded-lg border px-3 font-inter-normal-12px",
        resultsOutdated
          ? "border-orange-200 bg-orange-50 text-orange-600"
          : "border-red-200 bg-red-50 text-red-600",
      )}
      data-loc="error-selector"
    >
      <Tooltip
        disabled={!resultsOutdated}
        placement="top"
        title={outdatedTooltipTitle}
        asChild
      >
        <div
          className={twJoin(
            "mr-3 flex items-center border-r py-2 pr-3",
            resultsOutdated ? "border-orange-200" : "border-red-200",
          )}
        >
          <button onClick={setPreviousError}>
            <Icon icon={faChevronLeft} size="xs" variant={iconVariant} />
          </button>

          <Icon icon={faWarning} size="xs" variant={iconVariant} />

          <span className="ml-0.5">
            {`${
              displayedFailureArrayIndex > -1
                ? displayedFailureArrayIndex + 1
                : "?"
            } of ${errors_count}${resultsOutdated ? " outdated" : ""} errors`}
          </span>
          <button onClick={setNextError}>
            <Icon icon={faChevronRight} size="xs" variant={iconVariant} />
          </button>
        </div>
      </Tooltip>
      <div className="flex min-w-0 flex-1 items-center justify-between py-2">
        <div className="error-selector-scrollbar max-h-full min-w-0 flex-1 overflow-auto break-words">
          {errorSections.map((section, index) => (
            <div
              key={section.title}
              className={twJoin(index < errorSections.length - 1 && "mb-3")}
            >
              <ErrorBlobDisplay
                errorBlob={section.errorBlob}
                isCodeBlock={section.isCodeBlock}
                title={section.title}
                variant={iconVariant}
              />
            </div>
          ))}
        </div>
        {action && (
          <button
            className="ml-3 whitespace-nowrap font-inter-medium-13px underline"
            onClick={action.onClick}
          >
            {action.text}
          </button>
        )}
        {!action && failureCount && (
          <span className="ml-3 flex-shrink-0 font-bold">
            {failureCount} failures
          </span>
        )}
      </div>
    </div>
  );
};
